import React, { useState } from 'react';
import { Modal, Form } from 'react-bootstrap';
import { handleJoinWaitlist, isValidEmail } from '../../helperFunctions';
import SurveyModal from '../SurveyModal/SurveyModal';
import CTAButton from '../../shared/CTAButton';
import './waitlistModal.css';

interface Props {
  show: boolean;
  onHide: () => void;
  setShowToast: (show: boolean) => void;
  setToastMessage: (message: string) => void;
}

const WaitlistModal: React.FC<Props> = ({
  show,
  onHide,
  setShowToast,
  setToastMessage,
}) => {
  const [email, setEmail] = useState('');
  const [showSurvey, setShowSurvey] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setEmail('');
    setShowSurvey(false);
    setSubmitting(false);
  };

  const handleClose = () => {
    onHide();
    setTimeout(reset, 200);
  };

  const submit = async () => {
    if (!isValidEmail(email)) {
      setShowToast(true);
      setToastMessage('Please enter a valid email address');
      return;
    }
    if (
      process.env.NODE_ENV === 'development' &&
      !email.endsWith('@testcrm.com') &&
      !email.endsWith('@morph-technologies.com') &&
      !email.endsWith('@example.com')
    ) {
      setShowToast(true);
      setToastMessage(
        'Please test with a @example.com or @morph-technologies.com email otherwise the email will be added to our CRM waitlist. Use @testcrm.com to actually add to the CRM.',
      );
      return;
    }

    setSubmitting(true);
    const opensSurvey = await new Promise<boolean>((resolve) => {
      handleJoinWaitlist(email, setShowToast, setToastMessage, resolve);
    });
    setSubmitting(false);

    if (opensSurvey) {
      setShowSurvey(true);
    } else {
      handleClose();
    }
  };

  return (
    <>
      <Modal
        show={show && !showSurvey}
        onHide={handleClose}
        centered
        dialogClassName="waitlist-modal"
      >
        <Modal.Body>
          <button
            type="button"
            className="waitlist-modal__close"
            onClick={handleClose}
            aria-label="Close"
          >
            ×
          </button>
          <p className="ui-eyebrow waitlist-modal__eyebrow">Free trial</p>
          <h3 className="waitlist-modal__title">Run your first job, on us.</h3>
          <p className="waitlist-modal__copy">
            Drop your email and we&apos;ll get you set up with Morph.
          </p>
          <Form
            onSubmit={(e) => {
              e.preventDefault();
              submit();
            }}
            className="waitlist-modal__form"
          >
            <Form.Control
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoFocus
              required
              disabled={submitting}
            />
            <CTAButton
              type="submit"
              variant="blue"
              size="lg"
              disabled={!email || submitting}
            >
              {submitting ? 'Submitting…' : 'Continue'}
            </CTAButton>
          </Form>
          <p className="waitlist-modal__fineprint">
            By continuing, you agree to our{' '}
            <a href="/privacy-policy">Privacy Policy</a> and{' '}
            <a href="/terms-conditions">Terms</a>.
          </p>
        </Modal.Body>
      </Modal>

      <SurveyModal
        show={show && showSurvey}
        onHide={handleClose}
        setShowToast={setShowToast}
        setToastMessage={setToastMessage}
        email={email}
        setEmail={setEmail}
      />
    </>
  );
};

export default WaitlistModal;
